import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

// Section anchors vary by route because / and /landing-page have different content.
const NAV_ITEMS_BY_PATH = {
  '/landing-page': [
    { label: 'Problem', anchor: '#problem', id: 'problem' },
    { label: 'Solution', anchor: '#solution', id: 'solution' },
    { label: 'Guides', anchor: '#guides', id: 'guides' },
    { label: 'Join Waitlist', anchor: '#waitlist', id: 'waitlist' }
  ],
  default: [
    { label: 'How It Works', anchor: '#how-it-works', id: 'how-it-works' },
    { label: 'Guides', anchor: '#four-guides', id: 'four-guides' },
    { label: 'Who It\'s For', anchor: '#who-its-for', id: 'who-its-for' },
    { label: 'Get Started', anchor: '#join-waitlist', id: 'join-waitlist' }
  ]
};

const MENU_TRANSITION_MS = 200;

const Header = () => {
  const location = useLocation();
  const navigationItems =
    NAV_ITEMS_BY_PATH[location.pathname] ?? NAV_ITEMS_BY_PATH.default;

  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMenuMounted, setIsMenuMounted] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const prefersReducedMotionRef = useRef(false);

  useEffect(() => {
    prefersReducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
  }, []);

  // Cheap, passive, rAF-throttled scroll tracking for the progress bar + glass header state.
  useEffect(() => {
    let ticking = false;

    const updateScrollState = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement?.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      setScrollProgress(progress);
      setIsScrolled(scrollTop > 20);
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateScrollState);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateScrollState();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Section-spy via IntersectionObserver instead of per-scroll getBoundingClientRect.
  useEffect(() => {
    const sectionIds = navigationItems?.map((item) => item?.id) ?? [];
    const elements = sectionIds
      ?.map((id) => document.getElementById(id))
      ?.filter(Boolean);

    if (!elements || elements?.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          ?.filter((entry) => entry?.isIntersecting)
          ?.sort((a, b) => b?.intersectionRatio - a?.intersectionRatio);

        if (visible?.[0]) {
          setActiveSection(visible?.[0]?.target?.id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [navigationItems]);

  // Mount the overlay in its CLOSED visual state first, then flip to visible on a
  // later frame — a node can't transition on the same frame it's inserted, so
  // setting isMenuVisible in the same tick as isMenuMounted would make the open
  // pop in instantly instead of animating. Close does the reverse: flip to hidden
  // immediately, then unmount after the transition finishes.
  useEffect(() => {
    let rafId;
    let timeoutId;
    const reduceMotion = prefersReducedMotionRef.current;

    if (isMobileMenuOpen) {
      setIsMenuMounted(true);

      if (reduceMotion) {
        setIsMenuVisible(true);
      } else {
        rafId = requestAnimationFrame(() => {
          rafId = requestAnimationFrame(() => setIsMenuVisible(true));
        });
      }
    } else {
      setIsMenuVisible(false);

      if (isMenuMounted) {
        if (reduceMotion) {
          setIsMenuMounted(false);
        } else {
          timeoutId = setTimeout(() => setIsMenuMounted(false), MENU_TRANSITION_MS);
        }
      }
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileMenuOpen]);

  // Lock body scroll while the mobile overlay is open.
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleAnchorClick = (anchor) => {
    const element = document.querySelector(anchor);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleWaitlistClick = () => {
    window.open('https://depthme.app', '_blank', 'noopener,noreferrer');
  };

  const prefersReducedMotion = prefersReducedMotionRef.current;

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div
        className="scroll-progress fixed top-0 left-0 h-1 bg-gradient-to-r from-primary to-accent z-[1000] transition-smooth"
        style={{ width: `${scrollProgress}%` }}
      />
      <header className={`fixed top-0 left-0 right-0 z-120 transition-smooth ${
        isScrolled ? 'glassmorphism' : 'bg-transparent'
      }`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div
            className="flex items-center justify-between h-20"
          >
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <img
                src="/brand/depthme-icon-192.png"
                alt="DepthMe Logo"
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-semibold text-foreground">DepthMe</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navigationItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleAnchorClick(item?.anchor)}
                  className={`text-sm font-medium transition-smooth hover:text-primary ${
                    activeSection === item?.id
                      ? 'text-primary' :'text-muted-foreground'
                  }`}
                >
                  {item?.label}
                </button>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center space-x-3">
              <Button
                variant="default"
                onClick={handleWaitlistClick}
                className="violet-glow"
              >
                Open App
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-foreground hover:text-primary transition-smooth"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <Icon
                name={isMobileMenuOpen ? "X" : "Menu"}
                size={24}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay — portalled to <body> so `fixed` is viewport-relative,
          not contained by the header's backdrop-filter (which creates a new containing block). */}
      {isMenuMounted && createPortal(
        <div
          className={`md:hidden fixed inset-0 z-110 flex flex-col bg-background/95 ${
            isMenuVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{
            height: '100dvh',
            paddingTop: 'calc(5rem + env(safe-area-inset-top))',
            paddingBottom: 'env(safe-area-inset-bottom)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            transform: prefersReducedMotion
              ? 'none'
              : isMenuVisible ? 'translateY(0)' : 'translateY(-8px)',
            transition: prefersReducedMotion
              ? 'opacity 0ms linear'
              : `opacity ${MENU_TRANSITION_MS}ms ease-out, transform ${MENU_TRANSITION_MS}ms ease-out`
          }}
        >
          <div className="flex flex-col items-center justify-center flex-1 space-y-2 overflow-y-auto">
            {navigationItems?.map((item) => (
              <button
                key={item?.id}
                onClick={() => handleAnchorClick(item?.anchor)}
                className={`min-h-[44px] px-6 flex items-center justify-center text-lg font-medium transition-smooth hover:text-primary ${
                  activeSection === item?.id
                    ? 'text-primary' :'text-muted-foreground'
                }`}
              >
                {item?.label}
              </button>
            ))}
            <div className="pt-4 flex flex-col items-center space-y-3">
              <Button
                variant="default"
                onClick={handleWaitlistClick}
                className="violet-glow min-h-[44px]"
              >
                Open App
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Header;
